/* File: globalSearch.jsx
 * Path: ananunadm/src/components/main/globalSearch.jsx
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
	Flex,
	useMantineColorScheme,
	Text,
	Modal,
	Button,
	ActionIcon,
	Image,
	Input,
	TextInput,
	Divider,
	Code,
	Loader,
	Avatar,
	Box,
	Tooltip,
	CopyButton,
	ScrollArea,
	Select,
	MultiSelect,
	Textarea,
	Group,
	Stack,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { T } from "@/helpers/translator";
import { useLang } from "@/helpers/language";
import { sortOptionsByLabel } from "@/helpers/sortUsersByName";
import { DEFAULT_USER_IMAGE_SRC, getUserImageSrc } from "@/helpers/uploadImage";
import {
	IconSearch,
	IconLayoutGrid,
	IconUser,
	IconMessages,
	IconMessage,
	IconFile,
	IconCalendarEvent,
	IconDeviceDesktop,
	IconDeviceLaptop,
	IconDeviceAnalytics,
	IconPrinter,
	IconKeyboard,
	IconMouse,
	IconRouter,
	IconPhone,
	IconCpu,
	IconServer,
	IconApps,
	IconDoor,
	IconFingerprint,
	IconShieldCheck,
	IconCamera,
	IconTicket,
	IconAlertCircle,
	IconBriefcase,
	IconCurrencyDollar,
	IconCreditCard,
	IconBuilding,
	IconId,
	IconUsers,
	IconPackage,
	IconShoppingCart,
	IconTag,
	IconLayoutKanban,
	IconMusic,
	IconMicrophone2,
	IconPlaylist,
	IconChecklist,
	IconStack2,
	IconExternalLink,
	IconLink,
	IconArrowRight,
	IconClock,
	IconPlus,
	IconTerminal2,
	IconChecks,
	IconBell,
	IconBook2,
} from "@tabler/icons-react";
import { useGlobal } from "@/helpers/global";
import { useMobile } from "@/helpers/is-mobile";
import classes from "@/theme/GradientButton.module.css";
import { getGradientBorderButtonStyles } from "@/theme/themeSettings";
import * as API from "@/helpers/api";
import config from "@/config";
import { format } from "date-fns";
import { useSocket } from "@/helpers/socket";
import { useUser } from "@/helpers/userContext";
import { useGlobalCalling } from "@/helpers/globalCalling";
import { emitSocketAck } from "@/components/chat/polls/pollClient";

// ── localStorage ──────────────────────────────────────────────────────────────

const LS_SEARCHES = "apit_gs_searches";
const LS_ITEMS    = "apit_gs_items";
const MAX_RECENT  = 6;

function lsLoad(key) {
	try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
}
function lsSaveSearch(q) {
	const prev = lsLoad(LS_SEARCHES).filter((s) => s !== q);
	localStorage.setItem(LS_SEARCHES, JSON.stringify([q, ...prev].slice(0, MAX_RECENT)));
}
function lsSaveItem(entry) {
	const prev = lsLoad(LS_ITEMS).filter((e) => e.key !== entry.key);
	localStorage.setItem(LS_ITEMS, JSON.stringify([entry, ...prev].slice(0, MAX_RECENT)));
}

// ── Menu search ───────────────────────────────────────────────────────────────

function searchMenu(obj, searchTerm, lang = "en") {
	const results = [];
	const query = searchTerm.toLowerCase().trim();
	if (!query || !obj) return results;
	const walk = (item) => {
		if (!item || typeof item !== "object") return;
		if (Array.isArray(item)) { item.forEach(walk); return; }
		if (item.name && item.route && !item.subMenu) {
			const name = item.name[lang] || item.name.en || "";
			if (name && name.toLowerCase().includes(query))
				results.push({ name, route: item.route, icon: item.icon });
		}
		if (item.menu) walk(item.menu);
		if (item.subMenu) walk(item.subMenu);
	};
	walk(obj);
	return results.filter((r, i, a) => i === a.findIndex((t) => t.route === r.route));
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ml(obj, lang) {
	if (!obj) return "";
	if (typeof obj === "string") return obj;
	return obj[lang] || obj.en || obj.hy || obj.ru || "";
}

function capitalizeCamel(key) {
	return key.charAt(0).toUpperCase() + key.slice(1);
}

function sectionTitleKey(sectionKey) {
	if (sectionKey === "files") return "Files";
	if (sectionKey === "knowledgeArticles") return "knowledgeArticles";
	return `globalSearchSection${capitalizeCamel(sectionKey)}`;
}

function filterChipTitleKey(chipKey) {
	if (chipKey == null) return "globalSearchFilterAll";
	if (chipKey === "files") return "Files";
	if (chipKey === "knowledge") return "knowledgeBase";
	return `globalSearchFilter${capitalizeCamel(chipKey)}`;
}

function safeFormat(date, fmt = "MMM d, yyyy") {
	try { return format(new Date(date), fmt); } catch { return "—"; }
}

function messagePlainText(value) {
	const html = String(value || "").replace(/<br\b[^>]*>|<\/(?:p|div|li|h[1-6]|blockquote|tr)>/gi, " ");
	const doc = new DOMParser().parseFromString(html, "text/html");
	doc.querySelectorAll("script, style").forEach((node) => node.remove());
	return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

function parseSlashInput(rawValue) {
	const value = String(rawValue || "").trim();
	if (!value.startsWith("/")) {
		return { isSlash: false, commandToken: "", args: "", normalized: "" };
	}
	const withoutSlash = value.slice(1).trimStart();
	if (!withoutSlash) {
		return { isSlash: true, commandToken: "", args: "", normalized: "" };
	}
	const [commandToken = "", ...rest] = withoutSlash.split(/\s+/);
	return {
		isSlash: true,
		commandToken,
		args: rest.join(" ").trim(),
		normalized: commandToken.toLowerCase(),
	};
}

const SLASH_COMMANDS = [
	{
		id: "task",
		command: "/task",
		icon: IconChecklist,
		title: "Create task",
		description: "Open Todo task creation with a prefilled title and notes.",
		aliases: ["task", "todo"],
	},
	{
		id: "ticket",
		command: "/ticket",
		icon: IconTicket,
		title: "Create ticket",
		description: "Ask for a few details, create a draft ticket, and open it in Ticket page.",
		aliases: ["ticket", "issue", "support"],
	},
	{
		id: "call",
		command: "/call",
		icon: IconPhone,
		title: "Start call",
		description: "Select one or more users and start an audio or video call.",
		aliases: ["call", "video", "audio", "meetingcall"],
	},
	{
		id: "meeting",
		command: "/meeting",
		icon: IconCalendarEvent,
		title: "Create meeting",
		description: "Open the calendar event composer with meeting details prefilled.",
		aliases: ["meeting", "event", "calendar"],
	},
	{
		id: "poll",
		command: "/poll",
		icon: IconChecks,
		title: "Create poll",
		description: "Pick a chat, add options, and post a poll.",
		aliases: ["poll", "vote", "survey"],
	},
	{
		id: "remind",
		command: "/remind",
		icon: IconBell,
		title: "Create reminder",
		description: "Open reminder creation with a prefilled title and time.",
		aliases: ["remind", "reminder"],
	},
];

function matchSlashCommands(query) {
	const parsed = parseSlashInput(query);
	if (!parsed.isSlash) return [];
	if (!parsed.normalized) return SLASH_COMMANDS;
	return SLASH_COMMANDS.filter((item) =>
		item.aliases.some((alias) => alias.startsWith(parsed.normalized) || alias.includes(parsed.normalized))
	);
}

// ── Commands (labels via translator; searchTerms keep EN for matching) ───────

const COMMAND_SPEC = [
	{ id: "goHr",          icon: IconUsers,          searchTerms: ["go to hr", "hr", "human resources"], action: (h) => h.push("/hr") },
	{ id: "goFinance",     icon: IconCurrencyDollar, searchTerms: ["go to finance", "finance"], action: (h) => h.push("/finance") },
	{ id: "goProjects",    icon: IconBriefcase,    searchTerms: ["go to projects", "projects", "project"], action: (h) => h.push("/database/project") },
	{ id: "goCrm",         icon: IconUsers,        searchTerms: ["go to crm", "crm", "client"], action: (h) => h.push("/crm") },
	{ id: "goIt",          icon: IconDeviceDesktop, searchTerms: ["go to it", "it", "equipment"], action: (h) => h.push("/it") },
	{ id: "goChat",        icon: IconMessages,     searchTerms: ["go to chat", "chat", "message"], action: (h) => h.push("/chat") },
	{ id: "goCalendar",    icon: IconCalendarEvent, searchTerms: ["go to calendar", "calendar"], action: (h) => h.push("/calendar") },
	{ id: "goTickets",     icon: IconTicket,       searchTerms: ["go to tickets", "tickets", "ticket"], action: (h) => h.push("/ticket-system") },
	{ id: "goRadio",       icon: IconMusic,        searchTerms: ["go to radio", "radio", "music"], action: (h) => h.push("/radio") },
	{ id: "goTodo",        icon: IconChecklist,    searchTerms: ["go to todo", "todo", "task"], action: (h) => h.push("/todo") },
	{ id: "newTicket",     icon: IconPlus,         searchTerms: ["new ticket"], action: (h) => h.push("/ticket-system/ticket/new") },
	{ id: "newProject",    icon: IconPlus,         searchTerms: ["new project"], action: (h) => h.push("/database/project/new") },
	{ id: "newTask",       icon: IconPlus,         searchTerms: ["new task"], action: (h) => h.push("/todo/task/add") },
	{ id: "newOrder",      icon: IconPlus,         searchTerms: ["new order"], action: (h) => h.push("/ecommerce/order/new") },
	{ id: "newContact",     icon: IconPlus,         searchTerms: ["new contact"], action: (h) => h.push("/crm/contact/new") },
	{ id: "messageUser",   icon: IconMessage,      searchTerms: ["message user", "message"], action: (h) => h.push("/chat") },
];

function buildCommands(lang) {
	return COMMAND_SPEC.map((c) => ({
		...c,
		label: T(`globalSearchCmd_${c.id}_label`, lang),
		desc: T(`globalSearchCmd_${c.id}_desc`, lang),
	}));
}

function matchCommands(query, commands) {
	const q = query.trim().toLowerCase();
	if (!q || q.length < 2) return [];
	return commands.filter((cmd) => {
		const label = cmd.label.toLowerCase();
		if (label.startsWith(q) || label.includes(q)) return true;
		return cmd.searchTerms.some((t) => t.includes(q) || q.includes(t) || t.startsWith(q));
	}).slice(0, 4);
}

// ── Filter chips ──────────────────────────────────────────────────────────────

const FILTER_CHIPS = [
	{ key: null,       sections: null },
	{ key: "people",   sections: ["users", "departments", "positions", "contacts", "customers", "contactGroups"] },
	{ key: "files",    sections: ["files"] },
	{ key: "knowledge", sections: ["knowledgeArticles"] },
	{ key: "chats",    sections: ["chats", "messages"] },
	{ key: "tickets",  sections: ["tickets", "problems"] },
	{ key: "devices",  sections: ["pcs", "laptops", "monitors", "printers", "keyboards", "mice", "switches", "routers", "telephones", "cpus", "gpus", "rams", "roms", "smbs", "software", "rooms", "attendanceDevices", "warrantyProviders", "cameras"] },
	{ key: "projects", sections: ["projects"] },
	{ key: "finance",  sections: ["financeTransactions", "financeCards", "financeCardIssuers", "financeCategories"] },
	{ key: "calendar", sections: ["calendarEvents"] },
	{ key: "ecom",     sections: ["products", "stores", "orders", "brands", "ecomCategories"] },
	{ key: "todo",     sections: ["todoTasks", "todoBoards", "todoCategories"] },
	{ key: "radio",    sections: ["tracks", "artists", "playlists"] },
];

// ── Result config ─────────────────────────────────────────────────────────────

const RESULT_CONFIG = {
	users:               { icon: IconUser,            color: "blue",   getTitle: (item, lang) => ml(item.name, lang) || item.username || "", getSub: (item) => item.username ? `@${item.username}` : item.email || null, getRoute: (item) => `/hr/user/${item._id}` },
	files:               { icon: IconFile,            color: "yellow", getTitle: (item) => item.name || item.relativePath || "—", getSub: (item) => item.relativePath || item.mimeType || null, getRoute: (item) => `/files/browser/provider/${item.providerId}${item.browserPath ? `/${String(item.browserPath).split("/").filter(Boolean).map(encodeURIComponent).join("/")}` : ""}` },
	knowledgeArticles:   { icon: IconBook2,           color: "indigo", getTitle: (item, lang) => ml(item.title, lang) || item.slug || "—", getSub: (item, lang) => ml(item.summary, lang) || item.routePath || null, getRoute: (item) => `/knowledge/articles/${item._id}` },
	chats:               { icon: IconMessages,        color: "teal",   getTitle: (item, lang) => item.name || (item.chatType === "dm" ? T("globalSearchDirectMessage", lang) : item.chatType) || "—", getSub: (item) => item.chatType || null, getRoute: (item) => `/chat/chat-group/${item._id}` },
	messages:            { icon: IconMessage,         color: "cyan",   getTitle: (item) => { const b = messagePlainText(item.previewText || item.body); return b.length > 70 ? b.slice(0, 70) + "…" : b || "—"; }, getSub: (item) => item.chatName || null, getRoute: (item) => `/chat/chat-group/${item.chatId}?messageId=${item._id}` },
	calendarEvents:      { icon: IconCalendarEvent,   color: "indigo", getTitle: (item) => item.title || "—", getSub: (item) => item.start ? safeFormat(item.start) : null, getRoute: (item) => `/calendar?eventId=${item._id}` },
	departments:         { icon: IconBuilding,        color: "grape",  getTitle: (item, lang) => ml(item.name, lang), getSub: null, getRoute: (item) => `/hr/department/${item._id}` },
	positions:           { icon: IconId,              color: "violet", getTitle: (item, lang) => ml(item.name, lang), getSub: null, getRoute: (item) => `/hr/position/${item._id}` },
	pcs:                 { icon: IconDeviceDesktop,   color: "dark",   getTitle: (item) => item.name || "—", getSub: (item) => item.ip || item.mac || null, getRoute: (item) => `/it/pc/${item._id}` },
	laptops:             { icon: IconDeviceLaptop,    color: "blue",   getTitle: (item) => item.name || "—", getSub: (item) => item.ip || item.mac || null, getRoute: (item) => `/it/laptop/${item._id}` },
	monitors:            { icon: IconDeviceAnalytics, color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/monitor/${item._id}` },
	printers:            { icon: IconPrinter,         color: "gray",   getTitle: (item) => item.name || "—", getSub: (item) => item.ip || null, getRoute: (item) => `/it/printer/${item._id}` },
	keyboards:           { icon: IconKeyboard,        color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/keyboard/${item._id}` },
	mice:                { icon: IconMouse,           color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/mouse/${item._id}` },
	switches:            { icon: IconRouter,          color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/switch/${item._id}` },
	routers:             { icon: IconRouter,          color: "gray",   getTitle: (item) => item.name || "—", getSub: (item) => item.ip || null, getRoute: (item) => `/it/router/${item._id}` },
	telephones:          { icon: IconPhone,           color: "cyan",   getTitle: (item) => item.name || "—", getSub: (item) => item.ip || item.username || null, getRoute: (item) => `/it/telephone/${item._id}` },
	cpus:                { icon: IconCpu,             color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/cpu/${item._id}` },
	gpus:                { icon: IconCpu,             color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/gpu/${item._id}` },
	rams:                { icon: IconServer,          color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/ram/${item._id}` },
	roms:                { icon: IconServer,          color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/rom/${item._id}` },
	smbs:                { icon: IconServer,          color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/it/smb/${item._id}` },
	software:            { icon: IconApps,            color: "gray",   getTitle: (item) => item.name || "—", getSub: (item) => item.version || null, getRoute: (item) => `/it/software/${item._id}` },
	rooms:               { icon: IconDoor,            color: "gray",   getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/office/rooms/${item._id}` },
	attendanceDevices:   { icon: IconFingerprint,     color: "gray",   getTitle: (item) => item.name || "—", getSub: (item) => item.ip || item.deviceId || null, getRoute: (item) => `/it/attendance-device/${item._id}` },
	warrantyProviders:   { icon: IconShieldCheck,     color: "gray",   getTitle: (item) => item.name || "—", getSub: (item) => item.contact || null, getRoute: (item) => `/it/warranty-provider/${item._id}` },
	cameras:             { icon: IconCamera,          color: "red",    getTitle: (item) => item.name || "—", getSub: (item) => item.ip || null, getRoute: (item) => `/security/cameras/${item._id}` },
	tickets:             { icon: IconTicket,          color: "orange", getTitle: (item) => item.ID != null ? `#${item.ID}${item.title ? ` · ${item.title}` : ""}` : item.title || "—", getSub: (item) => item.createdAt ? safeFormat(item.createdAt) : null, getRoute: (item) => `/ticket-system/ticket/${item._id}` },
	problems:            { icon: IconAlertCircle,     color: "orange", getTitle: (item, lang) => ml(item.name, lang), getSub: null, getRoute: (item) => `/ticket-system/problem/${item._id}` },
	projects:            { icon: IconBriefcase,       color: "yellow", getTitle: (item) => item.ID || item.orderNumber || item.address || "—", getSub: (item) => item.address && item.ID ? item.address : null, getRoute: (item) => `/database/project/edit/${item._id}` },
	financeTransactions: { icon: IconCurrencyDollar,  color: "green",  getTitle: (item) => item.reference || item.description || "—", getSub: (item) => item.vendor || null, getRoute: (item) => `/finance/transaction/${item._id}` },
	financeCards:        { icon: IconCreditCard,      color: "green",  getTitle: (item) => item.name || "—", getSub: (item) => item.number ? `•••• ${item.number.slice(-4)}` : null, getRoute: (item) => `/finance/card/${item._id}` },
	financeCardIssuers:  { icon: IconCreditCard,      color: "green",  getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/finance/card-issuer/${item._id}` },
	financeCategories:   { icon: IconStack2,          color: "green",  getTitle: (item, lang) => ml(item.name, lang), getSub: null, getRoute: (item) => `/finance/category/${item._id}` },
	contacts:            { icon: IconUsers,           color: "pink",   getTitle: (item) => [item.firstName, item.lastName].filter(Boolean).join(" ") || item.company || "—", getSub: (item) => item.email || item.phone || null, getRoute: (item) => `/crm/contact/${item._id}` },
	customers: { icon: IconUsers, color: "pink", getTitle: (item) => [item.firstName, item.lastName].filter(Boolean).join(" ") || item.email || "—", getSub: (item) => item.email || item.phone || null, getRoute: () => "/crm/contact" },
	stores: { icon: IconBuilding, color: "lime", getTitle: (item, lang) => ml(item.name, lang) || item.merchant?.name || "—", getSub: (item) => item.href || null, getRoute: (item) => `/ecommerce/store/${item._id}` },
	contactGroups:        { icon: IconBuilding,        color: "pink",   getTitle: (item, lang) => ml(item.name, lang), getSub: null, getRoute: (item) => `/crm/contact-group/${item._id}` },
	products:            { icon: IconPackage,         color: "lime",   getTitle: (item, lang) => ml(item.name, lang) || item.reference?.name || item.code || "—", getSub: (item) => item.code || null, getRoute: (item) => `/ecommerce/product/${item._id}` },
	orders:              { icon: IconShoppingCart,    color: "lime",   getTitle: (item) => item.number ? `#${item.number}` : "—", getSub: (item) => item.desc || null, getRoute: (item) => `/ecommerce/order/${item._id}` },
	brands:              { icon: IconTag,             color: "lime",   getTitle: (item, lang) => ml(item.name, lang), getSub: null, getRoute: (item) => `/ecommerce/brand/${item._id}` },
	ecomCategories:      { icon: IconStack2,          color: "lime",   getTitle: (item, lang) => ml(item.name, lang), getSub: null, getRoute: (item) => `/ecommerce/category/${item._id}` },
	tracks:              { icon: IconMusic,           color: "grape",  getTitle: (item) => item.name || "—", getSub: (item) => item.artist || null, getRoute: (item) => `/radio/track/${item._id}` },
	artists:             { icon: IconMicrophone2,     color: "grape",  getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/radio/artist/${item._id}` },
	playlists:           { icon: IconPlaylist,        color: "grape",  getTitle: (item) => item.name || "—", getSub: null, getRoute: (item) => `/radio/playlist/${item._id}` },
	todoTasks:           { icon: IconChecklist,       color: "cyan",   getTitle: (item, lang) => ml(item.title, lang) || "—", getSub: (item, lang) => [ml(item.board?.name, lang), ml(item.category?.name, lang)].filter(Boolean).join(" · ") || null, getRoute: (item) => `/todo/task/${item._id}` },
	todoBoards:          { icon: IconLayoutKanban,    color: "cyan",   getTitle: (item, lang) => ml(item.name, lang), getSub: (item, lang) => ml(item.desc, lang) || null, getRoute: (item) => `/todo/board/${item._id}` },
	todoCategories:      { icon: IconStack2,          color: "cyan",   getTitle: (item, lang) => ml(item.name, lang) || "—", getSub: (item, lang) => (item.board || []).map((board) => ml(board?.name, lang)).filter(Boolean).join(", ") || null, getRoute: (item) => `/todo/category/${item._id}` },
};

const SECTION_ORDER = [
	"users", "files", "knowledgeArticles", "chats", "messages", "calendarEvents",
	"departments", "positions",
	"pcs", "laptops", "monitors", "printers", "keyboards", "mice", "switches", "routers", "telephones",
	"cpus", "gpus", "rams", "roms", "smbs", "software", "rooms",
	"attendanceDevices", "warrantyProviders", "cameras",
	"tickets", "problems",
	"projects",
	"financeTransactions", "financeCards", "financeCardIssuers", "financeCategories",
	"contacts", "customers", "contactGroups",
	"products", "stores", "orders", "brands", "ecomCategories",
	"tracks", "artists", "playlists",
	"todoTasks", "todoBoards", "todoCategories",
];

const SUGGESTION_KEYS = [
	"globalSearchSuggest1",
	"globalSearchSuggest2",
	"globalSearchSuggest3",
	"globalSearchSuggest4",
	"globalSearchSuggest5",
	"globalSearchSuggest6",
];

// ── Preview content ───────────────────────────────────────────────────────────

function PreviewRow({ label, value }) {
	return (
		<Flex gap={6} style={{ marginBottom: 5 }}>
			<Text size="xs" color="dimmed" style={{ flexShrink: 0, width: 72 }}>{label}:</Text>
			<Text size="xs" style={{ wordBreak: "break-all" }}>{value}</Text>
		</Flex>
	);
}

function PreviewContent({ flatItem, lang, colorScheme }) {
	const P = (key) => T(key, lang);

	if (!flatItem) {
		return (
			<Flex align="center" justify="center" style={{ height: "100%", minHeight: 200, padding: 24, opacity: 0.35 }}>
				<Flex direction="column" align="center" gap={8}>
					<IconSearch size={32} />
					<Text size="xs" color="dimmed" align="center">{P("globalSearchSelectPreview")}</Text>
				</Flex>
			</Flex>
		);
	}

	if (flatItem.type === "command") {
		const { command } = flatItem;
		const CmdIcon = command.icon;
		return (
			<Flex direction="column" align="center" justify="center" style={{ minHeight: 200, padding: 24, gap: 12 }}>
				<Avatar size={56} color="blue" radius="xl"><CmdIcon size={28} /></Avatar>
				<Text size="sm" weight={600} align="center">{command.label}</Text>
				<Text size="xs" color="dimmed" align="center">{command.desc}</Text>
				<Box style={{ padding: "2px 8px", background: "rgba(59,130,246,0.15)", borderRadius: 10, fontSize: 11, color: "#60a5fa" }}>{P("globalSearchCommandBadge")}</Box>
			</Flex>
		);
	}

	if (flatItem.type === "slash") {
		const { slash } = flatItem;
		const SlashIcon = slash.icon;
		return (
			<Flex direction="column" align="center" justify="center" style={{ minHeight: 200, padding: 24, gap: 12 }}>
				<Avatar size={56} color="grape" radius="xl"><SlashIcon size={28} /></Avatar>
				<Text size="sm" weight={600} align="center">{slash.command}</Text>
				<Text size="xs" color="dimmed" align="center">{slash.description}</Text>
				<Box style={{ padding: "2px 8px", background: "rgba(168,85,247,0.15)", borderRadius: 10, fontSize: 11, color: "#c084fc" }}>
					Slash command
				</Box>
			</Flex>
		);
	}

	if (flatItem.type === "page") {
		const { page } = flatItem;
		return (
			<Flex direction="column" align="center" justify="center" style={{ minHeight: 200, padding: 24, gap: 12 }}>
				{page.icon ? (
					<Image src={`/assets/${page.icon}`} alt={page.name} style={{ width: 48, height: 48, filter: `invert(${colorScheme === "dark" ? 1 : 0})` }} />
				) : (
					<Avatar size={56} color="indigo" radius="xl"><IconLayoutGrid size={28} /></Avatar>
				)}
				<Text size="sm" weight={600} align="center">{page.name}</Text>
				<Box style={{ padding: "2px 8px", background: "rgba(99,102,241,0.15)", borderRadius: 10, fontSize: 11, color: "#818cf8" }}>{P("globalSearchPageBadge")}</Box>
			</Flex>
		);
	}

	const { sectionKey, item, cfg } = flatItem;
	const SIcon = cfg.icon;
	const title = cfg.getTitle(item, lang);
	const sub = cfg.getSub ? cfg.getSub(item, lang) : null;

	let extras = null;
	if (sectionKey === "users") {
		extras = (
			<>
				{item.email && <PreviewRow label={P("globalSearchPreviewEmail")} value={item.email} />}
				{item.username && <PreviewRow label={P("globalSearchPreviewUsername")} value={`@${item.username}`} />}
				{item.phone && <PreviewRow label={P("globalSearchPreviewPhone")} value={item.phone} />}
				{typeof item.index === "number" && <PreviewRow label={P("globalSearchPreviewEmployeeNumber")} value={String(item.index)} />}
			</>
		);
	} else if (sectionKey === "tickets") {
		const ticketDescription = item.desc || item.description;
		extras = (
			<>
				{item.ID != null && <PreviewRow label="ID" value={String(item.ID)} />}
				{ticketDescription && <Text size="xs" color="dimmed" style={{ marginBottom: 8, display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{ticketDescription}</Text>}
				{item.createdAt && <PreviewRow label={P("globalSearchPreviewCreated")} value={safeFormat(item.createdAt)} />}
			</>
		);
	} else if (sectionKey === "messages") {
		const messageText = messagePlainText(item.body || item.previewText);
		extras = (
			<>
				{item.chatName && <PreviewRow label={P("globalSearchPreviewChat")} value={item.chatName} />}
				{messageText && <Text size="xs" color="dimmed" style={{ display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden", marginBottom: 4 }}>{messageText}</Text>}
				{item.createdAt && <PreviewRow label={P("globalSearchPreviewSent")} value={safeFormat(item.createdAt, "MMM d, yyyy HH:mm")} />}
			</>
		);
	} else if (sectionKey === "calendarEvents") {
		extras = (
			<>
				{item.start && <PreviewRow label={P("globalSearchPreviewStart")} value={safeFormat(item.start, "MMM d, yyyy HH:mm")} />}
				{item.end && <PreviewRow label={P("globalSearchPreviewEnd")} value={safeFormat(item.end, "MMM d, yyyy HH:mm")} />}
				{item.location && <PreviewRow label={P("globalSearchPreviewLocation")} value={item.location} />}
				{item.desc && <Text size="xs" color="dimmed" style={{ marginTop: 4, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.desc}</Text>}
			</>
		);
	} else if (["pcs", "laptops", "monitors", "printers", "keyboards", "mice", "switches", "routers", "telephones", "cameras", "attendanceDevices"].includes(sectionKey)) {
		extras = (
			<>
				{item.ip && <PreviewRow label={P("globalSearchPreviewIp")} value={item.ip} />}
				{item.mac && <PreviewRow label={P("globalSearchPreviewMac")} value={item.mac} />}
				{item.deviceId && <PreviewRow label={P("globalSearchPreviewDeviceId")} value={item.deviceId} />}
			</>
		);
	} else if (sectionKey === "contacts") {
		extras = (
			<>
				{item.company && <PreviewRow label={P("globalSearchPreviewCompany")} value={item.company} />}
				{item.email && <PreviewRow label={P("globalSearchPreviewEmail")} value={item.email} />}
				{item.phone && <PreviewRow label={P("globalSearchPreviewPhone")} value={item.phone} />}
			</>
		);
	} else if (sectionKey === "projects") {
		extras = (
			<>
				{item.address && <PreviewRow label={P("globalSearchPreviewAddress")} value={item.address} />}
				{item.orderNumber && <PreviewRow label={P("globalSearchPreviewOrderNumber")} value={item.orderNumber} />}
			</>
		);
	} else if (sectionKey === "financeTransactions") {
		extras = (
			<>
				{item.vendor && <PreviewRow label={P("globalSearchPreviewVendor")} value={item.vendor} />}
				{item.description && <Text size="xs" color="dimmed" style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.description}</Text>}
			</>
		);
	} else if (sectionKey === "financeCards") {
		extras = item.number ? <PreviewRow label={P("globalSearchPreviewNumber")} value={`•••• ${item.number.slice(-4)}`} /> : null;
	} else if (sectionKey === "software") {
		extras = item.version ? <PreviewRow label={P("globalSearchPreviewVersion")} value={item.version} /> : null;
	} else if (sectionKey === "tracks") {
		extras = item.artist ? <PreviewRow label={P("globalSearchPreviewArtist")} value={item.artist} /> : null;
	} else if (sectionKey === "todoTasks") {
		const assignees = (item.assignedTo || [])
			.map((person) => ml(person?.name, lang) || [ml(person?.firstName, lang), ml(person?.lastName, lang)].filter(Boolean).join(" ") || person?.username)
			.filter(Boolean)
			.join(", ");
		extras = (
			<>
				{ml(item.desc, lang) && <Text size="xs" color="dimmed" style={{ marginBottom: 8, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{ml(item.desc, lang)}</Text>}
				{ml(item.board?.name, lang) && <PreviewRow label={T("todoBoard", lang)} value={ml(item.board.name, lang)} />}
				{ml(item.category?.name, lang) && <PreviewRow label={T("todoCategory", lang)} value={ml(item.category.name, lang)} />}
				{item.priority && <PreviewRow label={T("priority", lang)} value={T(item.priority, lang)} />}
				{item.status && <PreviewRow label={T("status", lang)} value={T(item.status, lang)} />}
				{assignees && <PreviewRow label={T("assignedTo", lang)} value={assignees} />}
				{item.dueDate && <PreviewRow label={T("dueDate", lang)} value={safeFormat(item.dueDate)} />}
				{item.createdAt && <PreviewRow label={P("globalSearchPreviewCreated")} value={safeFormat(item.createdAt)} />}
			</>
		);
	} else if (sectionKey === "todoBoards") {
		const owner = ml(item.owner?.name, lang) || [ml(item.owner?.firstName, lang), ml(item.owner?.lastName, lang)].filter(Boolean).join(" ") || item.owner?.username;
		extras = (
			<>
				{ml(item.desc, lang) && <Text size="xs" color="dimmed" style={{ marginBottom: 8, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{ml(item.desc, lang)}</Text>}
				{owner && <PreviewRow label={T("owner", lang)} value={owner} />}
				<PreviewRow label={T("isPublic", lang)} value={T(item.isPublic ? "yes" : "no", lang)} />
				<PreviewRow label={T("archived", lang)} value={T(item.archived ? "yes" : "no", lang)} />
				{item.createdAt && <PreviewRow label={P("globalSearchPreviewCreated")} value={safeFormat(item.createdAt)} />}
			</>
		);
	} else if (sectionKey === "todoCategories") {
		const boardNames = (item.board || []).map((board) => ml(board?.name, lang)).filter(Boolean).join(", ");
		const responsibleNames = (item.responsible || [])
			.map((person) => ml(person?.name, lang) || [ml(person?.firstName, lang), ml(person?.lastName, lang)].filter(Boolean).join(" ") || person?.username)
			.filter(Boolean)
			.join(", ");
		extras = (
			<>
				{boardNames && <PreviewRow label={T("todoBoard", lang)} value={boardNames} />}
				{responsibleNames && <PreviewRow label={T("columnResponsible", lang)} value={responsibleNames} />}
				{item.color && <PreviewRow label={T("color", lang)} value={item.color} />}
				{item.createdAt && <PreviewRow label={P("globalSearchPreviewCreated")} value={safeFormat(item.createdAt)} />}
			</>
		);
	} else if (sub) {
		extras = <PreviewRow label={P("globalSearchPreviewInfo")} value={sub} />;
	}

	return (
		<Box style={{ padding: 16 }}>
			<Box style={{ padding: "2px 8px", border: "1px solid rgba(128,128,128,0.25)", borderRadius: 10, fontSize: 11, opacity: 0.7, display: "inline-block", marginBottom: 10 }}>
				{P(sectionTitleKey(sectionKey))}
			</Box>
			<Flex align="flex-start" gap={10} style={{ marginBottom: 12 }}>
				<Avatar
					size={36}
					color={cfg.color}
					radius="sm"
					style={{ flexShrink: 0 }}
					src={sectionKey === "users" ? getUserImageSrc(item, { targetWidth: 72 }) : undefined}
				>
					<SIcon size={18} />
				</Avatar>
				<Box style={{ minWidth: 0 }}>
					<Text size="sm" weight={600} style={{ wordBreak: "break-word" }}>{title}</Text>
					{sub && <Text size="xs" color="dimmed">{sub}</Text>}
				</Box>
			</Flex>
			{extras}
		</Box>
	);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ResultItem({ children, onClick, colorScheme, highlighted, id, onMouseEnter, onMouseLeave }) {
	return (
		<li
			id={id}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onClick={onClick}
			style={{
				listStyleType: "none",
				margin: 0,
				cursor: "pointer",
				padding: "7px 10px",
				borderRadius: 8,
				background: highlighted
					? colorScheme === "dark"
						? "linear-gradient(165deg,rgba(255,255,255,0.12) 0%,rgba(255,255,255,0.05) 100%)"
						: "linear-gradient(165deg,rgba(255,255,255,0.92) 0%,rgba(255,255,255,0.76) 100%)"
					: "transparent",
				border: highlighted
					? `1px solid ${colorScheme === "dark" ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.10)"}`
					: "1px solid transparent",
				boxShadow: highlighted
					? colorScheme === "dark" ? "0 4px 14px rgba(0,0,0,0.28)" : "0 4px 12px rgba(0,0,0,0.08)"
					: "none",
				backdropFilter: highlighted ? "blur(8px)" : "none",
				WebkitBackdropFilter: highlighted ? "blur(8px)" : "none",
				transition: "all 0.12s ease",
			}}
		>
			{children}
		</li>
	);
}

function SectionHeader({ Icon, label }) {
	return (
		<Flex align="center" gap={6} style={{ padding: "10px 10px 3px" }}>
			<Icon size={13} style={{ opacity: 0.55 }} />
			<Text size="xs" weight={600} color="dimmed" tt="uppercase" style={{ letterSpacing: "0.04em" }}>
				{label}
			</Text>
		</Flex>
	);
}

function FilterChip({ label, active, onClick }) {
	return (
		<button
			onClick={onClick}
			style={{
				padding: "3px 10px",
				borderRadius: 20,
				border: active ? "1px solid rgba(59,130,246,0.6)" : "1px solid rgba(128,128,128,0.25)",
				cursor: "pointer",
				fontSize: 12,
				fontWeight: 500,
				background: active ? "rgba(59,130,246,0.18)" : "transparent",
				color: "inherit",
				transition: "all 0.12s ease",
				whiteSpace: "nowrap",
				fontFamily: "inherit",
			}}
		>
			{label}
		</button>
	);
}

// ── Main component ────────────────────────────────────────────────────────────

function GlobalSearch() {
	const { lang } = useLang();
	const [opened, OpenClose] = useDisclosure(false);
	const { platforms } = useGlobal();
	const history = useHistory();
	const { colorScheme } = useMantineColorScheme();
	const isMobile = useMobile();
	const { socket } = useSocket();
	const { user } = useUser();
	const { startCall, startConference } = useGlobalCalling() || {};

	const [searchQuery, setSearchQuery]   = useState("");
	const [debouncedQuery]                = useDebouncedValue(searchQuery, 400);
	const [activeIndex, setActiveIndex]   = useState(-1);
	const [hoveredKey, setHoveredKey]     = useState(null);
	const [showPreview, setShowPreview]   = useState(true);
	const [activeFilter, setActiveFilter] = useState(null);

	const [menuResults, setMenuResults] = useState([]);
	const [dataResults, setDataResults] = useState({});
	const [loading, setLoading]         = useState(false);

	const [recentSearches, setRecentSearches] = useState([]);
	const [recentItems, setRecentItems]       = useState([]);
	const [slashModal, setSlashModal] = useState(null);
	const [slashSubmitting, setSlashSubmitting] = useState(false);
	const [slashUsers, setSlashUsers] = useState([]);
	const [slashChats, setSlashChats] = useState([]);
	const [slashForm, setSlashForm] = useState({
		title: "",
		description: "",
		domain: "",
		ticketType: "incident",
		callType: "audio",
		participants: [],
		meetingDate: "",
		meetingStart: "09:00",
		meetingEnd: "10:00",
		pollChatId: "",
		pollQuestion: "",
		pollOptions: ["", ""],
		remindDate: "",
		remindTime: "09:00",
	});

	useEffect(() => { if (isMobile) setShowPreview(false); }, [isMobile]);

	useEffect(() => {
		if (opened) {
			setRecentSearches(lsLoad(LS_SEARCHES));
			setRecentItems(lsLoad(LS_ITEMS));
		}
	}, [opened]);

	useEffect(() => {
		setMenuResults(searchMenu(platforms, searchQuery, lang));
		setActiveIndex(-1);
	}, [searchQuery, platforms, lang]);

	useEffect(() => {
		const slashMeta = parseSlashInput(debouncedQuery);
		if (slashMeta.isSlash || !debouncedQuery || debouncedQuery.trim().length < 2) { setDataResults({}); return; }
		let cancelled = false;
		setLoading(true);
		API.get({ route: config.api.search.global, query: { q: debouncedQuery.trim(), limit: 4 } })
			.then((res) => { if (!cancelled) setDataResults(res || {}); })
			.catch(() => { if (!cancelled) setDataResults({}); })
			.finally(() => { if (!cancelled) setLoading(false); });
		return () => { cancelled = true; };
	}, [debouncedQuery]);

	useEffect(() => {
		const handler = (e) => {
			if ((e.ctrlKey || e.metaKey) && e.key === "k") { e.preventDefault(); OpenClose.toggle(); }
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [OpenClose]);

	useEffect(() => {
		const el = document.createElement("style");
		el.textContent = getGradientBorderButtonStyles(colorScheme);
		document.head.appendChild(el);
		return () => document.head.removeChild(el);
	}, [colorScheme]);

	const close = useCallback(() => {
		OpenClose.close();
		setSearchQuery("");
		setDataResults({});
		setActiveIndex(-1);
		setHoveredKey(null);
		setActiveFilter(null);
	}, [OpenClose]);

	useEffect(() => {
		if (!opened || !user?._id) return;
		let cancelled = false;
		(async () => {
			try {
				const [rawUsers, rawChats] = await Promise.all([
					API.get({ route: config.api.hr.user, limit: 200 }),
					API.get({ route: config.api.chat.chatsWithMessages, query: { userId: user._id } }),
				]);
				if (cancelled) return;
				setSlashUsers(Array.isArray(rawUsers) ? rawUsers : []);
				setSlashChats(Array.isArray(rawChats) ? rawChats : []);
			} catch (error) {
				if (!cancelled) {
					setSlashUsers([]);
					setSlashChats([]);
				}
			}
		})();
		return () => { cancelled = true; };
	}, [opened, user?._id]);

	// ── Computed ─────────────────────────────────────────────────────────────

	const commands = useMemo(() => buildCommands(lang), [lang]);
	const commandResults = useMemo(() => matchCommands(searchQuery, commands), [searchQuery, commands]);
	const slashMeta = useMemo(() => parseSlashInput(searchQuery), [searchQuery]);
	const slashResults = useMemo(() => matchSlashCommands(searchQuery), [searchQuery]);

	const allDataSections = useMemo(() =>
		SECTION_ORDER
			.filter((key) => dataResults[key]?.length > 0)
			.map((key) => ({ key, items: dataResults[key], cfg: RESULT_CONFIG[key] }))
			.filter((s) => s.cfg),
		[dataResults]
	);

	const dataSections = useMemo(() => {
		if (!activeFilter) return allDataSections;
		const chip = FILTER_CHIPS.find((c) => c.key === activeFilter);
		if (!chip?.sections) return allDataSections;
		return allDataSections.filter((s) => chip.sections.includes(s.key));
	}, [allDataSections, activeFilter]);

	// Only show chips that have results (plus "All")
	const availableChips = useMemo(() => {
		if (!searchQuery) return [];
		return FILTER_CHIPS.filter((chip) =>
			chip.key === null || chip.sections?.some((s) => dataResults[s]?.length > 0)
		);
	}, [searchQuery, dataResults]);

	// Flat list for keyboard nav — commands and pages hidden when filter is active
	const flatItems = useMemo(() => {
		const items = [];
		if (slashMeta.isSlash) {
			slashResults.forEach((cmd) => items.push({ type: "slash", key: `slash-${cmd.id}`, slash: cmd }));
		} else if (!activeFilter) {
			commandResults.forEach((cmd) => items.push({ type: "command", key: `cmd-${cmd.id}`, command: cmd }));
			menuResults.forEach((page, i) => items.push({ type: "page", key: `page-${i}`, page }));
		}
		dataSections.forEach(({ key: sk, items: si, cfg }) => {
			si.forEach((item) => items.push({ type: "data", key: `${sk}-${item._id}`, sectionKey: sk, item, cfg }));
		});
		return items;
	}, [commandResults, menuResults, dataSections, activeFilter, slashMeta.isSlash, slashResults]);

	const flatIndexByKey = useMemo(() => {
		const map = {};
		flatItems.forEach((fi, i) => { map[fi.key] = i; });
		return map;
	}, [flatItems]);

	const activeIndexFromKeyboardRef = useRef(false);

	useEffect(() => {
		if (activeIndex >= 0 && activeIndexFromKeyboardRef.current) {
			const el = document.getElementById(`gs-item-${activeIndex}`);
			if (el) el.scrollIntoView({ block: "nearest" });
		}
		activeIndexFromKeyboardRef.current = false;
	}, [activeIndex]);

	const slashModalContentRef = useRef(null);

	const focusSlashModalFirstField = useCallback(() => {
		requestAnimationFrame(() => {
			const root = slashModalContentRef.current;
			if (!root) return;
			const field = root.querySelector(
				'input:not([type="hidden"]):not([disabled]), textarea:not([disabled])'
			);
			field?.focus?.();
		});
	}, []);

	const openSlashModal = useCallback((slash) => {
		const today = safeFormat(new Date(), "yyyy-MM-dd");
		const parsed = parseSlashInput(searchQuery);
		const initialArgs = parsed.args || "";
		setSlashForm({
			title: initialArgs,
			description: "",
			domain: "",
			ticketType: "incident",
			callType: "audio",
			participants: [],
			meetingDate: today,
			meetingStart: "09:00",
			meetingEnd: "10:00",
			pollChatId: "",
			pollQuestion: initialArgs,
			pollOptions: ["", ""],
			remindDate: today,
			remindTime: "09:00",
		});
		setSlashModal(slash);
	}, [searchQuery]);

	const ensureDirectChat = useCallback(async (partnerId) => {
		const existing = slashChats.find((chat) =>
			chat?.chatType === "direct" &&
			Array.isArray(chat?.users) &&
			chat.users.some((member) => String(member?._id || member) === String(user?._id)) &&
			chat.users.some((member) => String(member?._id || member) === String(partnerId))
		);
		if (existing?._id) return existing;
		return API.post(config.api.chat.chatGroup, {
			users: [user?._id, partnerId],
			admins: [user?._id],
			privateChat: true,
			readOnly: false,
			chatType: "direct",
			createdAt: new Date(),
			updated: new Date()
		});
	}, [slashChats, user?._id]);

	const submitSlashModal = useCallback(async () => {
		if (!slashModal) return;
		setSlashSubmitting(true);
		try {
			if (slashModal.id === "task") {
				const params = new URLSearchParams();
				if (slashForm.title.trim()) params.set("title", slashForm.title.trim());
				if (slashForm.description.trim()) params.set("desc", slashForm.description.trim());
				close();
				setSlashModal(null);
				history.push(`/todo/task/add${params.toString() ? `?${params.toString()}` : ""}`);
				return;
			}

			if (slashModal.id === "ticket") {
				const draft = await API.post(config.api.ticketSystem.ticket, { temporary: true });
				const params = new URLSearchParams({ temporary: "true" });
				if (slashForm.domain) params.set("domain", slashForm.domain);
				if (slashForm.ticketType) params.set("type", slashForm.ticketType);
				if (slashForm.title.trim()) params.set("title", slashForm.title.trim());
				if (slashForm.description.trim()) params.set("desc", slashForm.description.trim());
				close();
				setSlashModal(null);
				history.push(`/ticket-system/ticket/${draft._id}?${params.toString()}`);
				return;
			}

			if (slashModal.id === "meeting") {
				const params = new URLSearchParams({ compose: "meeting" });
				if (slashForm.title.trim()) params.set("title", slashForm.title.trim());
				if (slashForm.description.trim()) params.set("desc", slashForm.description.trim());
				if (slashForm.meetingDate) params.set("date", slashForm.meetingDate);
				if (slashForm.meetingStart) params.set("startTime", slashForm.meetingStart);
				if (slashForm.meetingEnd) params.set("endTime", slashForm.meetingEnd);
				close();
				setSlashModal(null);
				history.push(`/calendar?${params.toString()}`);
				return;
			}

			if (slashModal.id === "remind") {
				const params = new URLSearchParams({ compose: "reminder" });
				if (slashForm.title.trim()) params.set("title", slashForm.title.trim());
				if (slashForm.description.trim()) params.set("desc", slashForm.description.trim());
				if (slashForm.remindDate) params.set("date", slashForm.remindDate);
				if (slashForm.remindTime) params.set("time", slashForm.remindTime);
				close();
				setSlashModal(null);
				history.push(`/calendar?${params.toString()}`);
				return;
			}

			if (slashModal.id === "call") {
				if (!slashForm.participants.length) {
					notifications.show({ color: "yellow", message: "Select at least one participant." });
					return;
				}
				const selectedUsers = slashUsers.filter((item) => slashForm.participants.includes(String(item._id)));
				const callType = slashForm.callType === "video" ? "video" : "audio";
				if (selectedUsers.length === 1) {
					const partner = selectedUsers[0];
					const directChat = await ensureDirectChat(String(partner._id));
					startCall?.(partner, callType, directChat?._id || null);
				} else {
					startConference?.({
						callType: callType === "video" ? "conference_video" : "conference_audio",
						invitedUsers: selectedUsers.map((item) => String(item._id)),
					});
				}
				close();
				setSlashModal(null);
				return;
			}

			if (slashModal.id === "poll") {
				const options = slashForm.pollOptions.map((item) => item.trim()).filter(Boolean);
				if (!slashForm.pollChatId) {
					notifications.show({ color: "yellow", message: "Choose a chat for the poll." });
					return;
				}
				if (!slashForm.pollQuestion.trim() || options.length < 2) {
					notifications.show({ color: "yellow", message: "Add a question and at least two options." });
					return;
				}
				await emitSocketAck(socket, "poll:create", {
					chatId: slashForm.pollChatId,
					question: slashForm.pollQuestion.trim(),
					description: slashForm.description.trim(),
					options: options.map((text, index) => ({
						clientKey: `slash-${Date.now()}-${index}`,
						text,
					})),
					isAnonymous: true,
					allowsMultipleAnswers: false,
					allowsRevote: false,
					hideResultsUntilClosed: false,
					shuffleOptions: false,
					allowAddingOptions: false,
					mode: "regular",
					correctOptionIds: [],
					explanation: "",
					closeDate: null,
					openPeriodSeconds: null,
				});
				close();
				setSlashModal(null);
				notifications.show({ color: "green", message: "Poll created successfully." });
			}
		} catch (error) {
			notifications.show({
				color: "red",
				message: error?.response?.data?.error || error?.message || "Unable to run slash command.",
			});
		} finally {
			setSlashSubmitting(false);
		}
	}, [
		close,
		ensureDirectChat,
		history,
		slashForm,
		slashModal,
		slashUsers,
		socket,
		startCall,
		startConference,
	]);

	const handleOpen = useCallback((flatItem, newTab = false) => {
		if (!flatItem) return;
		if (flatItem.type === "slash") { openSlashModal(flatItem.slash); return; }
		if (flatItem.type === "command") { close(); flatItem.command.action(history); return; }
		let route = null;
		let entry = null;
		if (flatItem.type === "page") {
			route = `/${flatItem.page.route}`;
			entry = { key: `page-${flatItem.page.route}`, title: flatItem.page.name, sub: null, route, iconKey: "page", color: "indigo" };
		} else if (flatItem.type === "data") {
			const { cfg, item, sectionKey } = flatItem;
			route = cfg.getRoute(item);
			const title = cfg.getTitle(item, lang);
			const sub = cfg.getSub ? cfg.getSub(item, lang) : null;
			entry = { key: `${sectionKey}-${item._id}`, title, sub, route, iconKey: sectionKey, color: cfg.color };
		}
		if (!route) return;
		lsSaveItem(entry);
		if (debouncedQuery.length >= 2) lsSaveSearch(debouncedQuery);
		if (newTab) { window.open(`${window.location.origin}${route}`, "_blank"); }
		else { close(); history.push(route); }
	}, [close, history, lang, debouncedQuery, openSlashModal]);

	useEffect(() => {
		if (!opened) return;
		const handler = (e) => {
			if (e.key === "ArrowDown") {
				e.preventDefault();
				activeIndexFromKeyboardRef.current = true;
				setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				activeIndexFromKeyboardRef.current = true;
				setActiveIndex((i) => Math.max(i - 1, -1));
			} else if (e.key === "Enter") {
				if (activeIndex >= 0 && activeIndex < flatItems.length) handleOpen(flatItems[activeIndex]);
			} else if (e.key === "Tab") {
				e.preventDefault();
				if (!isMobile) setShowPreview((v) => !v);
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [opened, flatItems, activeIndex, handleOpen, isMobile]);

	const activeItem    = activeIndex >= 0 ? flatItems[activeIndex] : null;
	const hasAnyResults = slashMeta.isSlash
		? slashResults.length > 0
		: commandResults.length > 0 || menuResults.length > 0 || dataSections.length > 0;
	const showEmpty     = !searchQuery;
	const showNoResults = searchQuery.length > 0 && !loading && !hasAnyResults;
	const userOptions = useMemo(
		() =>
			sortOptionsByLabel(
				slashUsers
					.filter((item) => String(item?._id) !== String(user?._id))
					.map((item) => ({
						value: String(item?._id),
						label: ml(item?.name, lang) || item?.username || item?.email || String(item?._id),
					})),
				lang
			),
		[slashUsers, user?._id, lang]
	);
	const chatOptions = useMemo(
		() =>
			slashChats.map((chat) => ({
				value: String(chat?._id),
				label: chat?.name || (chat?.chatType === "direct" ? "Direct chat" : `Chat ${String(chat?._id).slice(-6)}`),
			})),
		[slashChats]
	);
	const pollChatOptions = useMemo(
		() => chatOptions.filter((option) => {
			const chat = slashChats.find((item) => String(item?._id) === String(option.value));
			return chat?.chatType !== "direct";
		}),
		[chatOptions, slashChats]
	);

	// ── Render ───────────────────────────────────────────────────────────────

	return (
		<>
			{isMobile ? (
				<ActionIcon
					aria-label={T("search", lang)}
					title={T("search", lang)}
					variant="subtle"
					size="md"
					radius="md"
					onClick={() => {
						if (!opened) OpenClose.open();
					}}
					style={{
						flexShrink: 0
					}}
				>
					<IconSearch size={18} color="grey" stroke={1.5} />
				</ActionIcon>
			) : (
				<Button
					className={`${classes.gradientButton} gradient-border-button`}
					data-selected={opened ? "true" : "false"}
					styles={{ label: { display: "flex", justifyContent: "space-between", width: "100%" } }}
					style={{ width: "100%", height: "40px", padding: 7, maxWidth: "200px", background: "transparent", marginLeft: 0 }}
					variant="outline" radius="md" size="md"
					onClick={OpenClose.toggle}
				>
					<IconSearch size={24} color="grey" />
					<Code>{T("globalSearchShortcutHint", lang)}</Code>
				</Button>
			)}

			<Modal
				opened={opened}
				onClose={close}
				size={isMobile ? "100%" : "920px"}
				fullScreen={isMobile}
				radius={isMobile ? 0 : "md"}
				overlayProps={{ opacity: 0.55, blur: 5 }}
				withCloseButton={isMobile}
				title={isMobile ? T("search", lang) : undefined}
				styles={{ body: { padding: 0 } }}
			>
				{/* Input */}
				<Box style={{ padding: "0 4px" }}>
					<Input
						placeholder={T("globalSearchPlaceholder", lang)}
						variant="unstyled"
						size="xl"
						icon={loading ? <Loader size={18} /> : <IconSearch size={24} />}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						autoFocus
					/>
					{!searchQuery && (
						<Text size="xs" color="dimmed" style={{ paddingLeft: 44, paddingBottom: 6 }}>
							{T("globalSearchSubtitle", lang)}
						</Text>
					)}
				</Box>

				{/* Filter chips — only appear when chips have data results */}
				{availableChips.length > 1 && (
					<Box style={{ padding: "0 12px 8px", overflowX: "auto" }}>
						<Flex gap={6} style={{ flexWrap: "nowrap", minWidth: "max-content" }}>
							{availableChips.map((chip) => (
								<FilterChip
									key={chip.key ?? "all"}
									label={T(filterChipTitleKey(chip.key), lang)}
									active={activeFilter === chip.key}
									onClick={() => setActiveFilter(activeFilter === chip.key ? null : chip.key)}
								/>
							))}
						</Flex>
					</Box>
				)}

				<Divider m="0" />

				{/* Body: left results + right preview */}
				<Flex style={{ minHeight: isMobile ? "60vh" : 320, overflow: "hidden" }}>
					{/* Left */}
					<Box style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
						<ScrollArea style={{ maxHeight: isMobile ? "72vh" : "60vh" }}>
							<ul style={{ padding: "6px 10px 12px", margin: 0 }}>

								{/* Empty state */}
								{showEmpty && (
									<>
										{recentSearches.length > 0 && (
											<>
												<SectionHeader Icon={IconClock} label={T("globalSearchRecentSearches", lang)} />
												{recentSearches.map((s, i) => (
													<ResultItem key={`rs-${i}`} colorScheme={colorScheme} highlighted={false} onClick={() => setSearchQuery(s)}>
														<Flex align="center" gap="sm">
															<Avatar size={26} radius="sm" color="gray"><IconClock size={13} /></Avatar>
															<Text size="sm">{s}</Text>
														</Flex>
													</ResultItem>
												))}
												<Divider my={4} />
											</>
										)}
										{recentItems.length > 0 && (
											<>
												<SectionHeader Icon={IconArrowRight} label={T("globalSearchRecentlyOpened", lang)} />
												{recentItems.map((entry, i) => {
													const ec = RESULT_CONFIG[entry.iconKey];
													const EIcon = ec ? ec.icon : IconArrowRight;
													const hk = `ri-${i}`;
													return (
														<ResultItem
															key={hk}
															colorScheme={colorScheme}
															highlighted={hoveredKey === hk}
															onMouseEnter={() => setHoveredKey(hk)}
															onMouseLeave={() => setHoveredKey(null)}
															onClick={() => { close(); history.push(entry.route); }}
														>
															<Flex align="center" gap="sm">
																<Avatar
																	size={26}
																	radius="sm"
																	color={entry.color}
																	src={entry.iconKey === "users" ? DEFAULT_USER_IMAGE_SRC : undefined}
																>
																	<EIcon size={13} />
																</Avatar>
																<Box style={{ minWidth: 0 }}>
																	<Text size="sm" weight={500} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.title}</Text>
																	{entry.sub && <Text size="xs" color="dimmed" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.sub}</Text>}
																</Box>
															</Flex>
														</ResultItem>
													);
												})}
												<Divider my={4} />
											</>
										)}
										<SectionHeader Icon={IconSearch} label={T("globalSearchTrySearchingFor", lang)} />
										{SUGGESTION_KEYS.map((sk, i) => {
											const hk = `sug-${i}`;
											const sugText = T(sk, lang);
											return (
												<ResultItem
													key={hk}
													colorScheme={colorScheme}
													highlighted={hoveredKey === hk}
													onMouseEnter={() => setHoveredKey(hk)}
													onMouseLeave={() => setHoveredKey(null)}
													onClick={() => setSearchQuery(sugText)}
												>
													<Flex align="center" gap="sm">
														<Avatar size={26} radius="sm" color="gray"><IconSearch size={13} /></Avatar>
														<Text size="sm" color="dimmed">{sugText}</Text>
													</Flex>
												</ResultItem>
											);
										})}
									</>
								)}

								{/* No results */}
								{showNoResults && (
									<Text color="dimmed" size="sm" align="center" style={{ padding: "32px 0" }}>
										{T("noResults", lang)}
									</Text>
								)}

								{/* Slash commands */}
								{slashMeta.isSlash && slashResults.length > 0 && (
									<>
										<SectionHeader Icon={IconTerminal2} label="Slash commands" />
										{slashResults.map((cmd) => {
											const fiKey = `slash-${cmd.id}`;
											const flatIdx = flatIndexByKey[fiKey] ?? -1;
											const highlighted = hoveredKey === fiKey || flatIdx === activeIndex;
											const CmdIcon = cmd.icon;
											return (
												<ResultItem
													key={fiKey}
													id={`gs-item-${flatIdx}`}
													colorScheme={colorScheme}
													highlighted={highlighted}
													onMouseEnter={() => { setHoveredKey(fiKey); setActiveIndex(flatIdx); }}
													onMouseLeave={() => setHoveredKey(null)}
													onClick={() => handleOpen({ type: "slash", key: fiKey, slash: cmd })}
												>
													<Flex align="center" gap="sm">
														<Avatar size={26} radius="sm" color="grape"><CmdIcon size={13} /></Avatar>
														<Box style={{ flex: 1, minWidth: 0 }}>
															<Text size="sm" weight={500}>{cmd.command}</Text>
															<Text size="xs" color="dimmed">{cmd.description}</Text>
														</Box>
														<Box style={{ padding: "1px 7px", background: "rgba(168,85,247,0.15)", borderRadius: 10, fontSize: 11, color: "#c084fc", flexShrink: 0 }}>
															Launch
														</Box>
													</Flex>
												</ResultItem>
											);
										})}
									</>
								)}

								{/* Commands */}
								{!slashMeta.isSlash && !activeFilter && commandResults.length > 0 && (
									<>
										<SectionHeader Icon={IconTerminal2} label={T("globalSearchCommands", lang)} />
										{commandResults.map((cmd) => {
											const fiKey = `cmd-${cmd.id}`;
											const flatIdx = flatIndexByKey[fiKey] ?? -1;
											const highlighted = hoveredKey === fiKey || flatIdx === activeIndex;
											const CmdIcon = cmd.icon;
											return (
												<ResultItem
													key={fiKey}
													id={`gs-item-${flatIdx}`}
													colorScheme={colorScheme}
													highlighted={highlighted}
													onMouseEnter={() => { setHoveredKey(fiKey); setActiveIndex(flatIdx); }}
													onMouseLeave={() => setHoveredKey(null)}
													onClick={() => handleOpen({ type: "command", key: fiKey, command: cmd })}
												>
													<Flex align="center" gap="sm">
														<Avatar size={26} radius="sm" color="blue"><CmdIcon size={13} /></Avatar>
														<Box style={{ flex: 1, minWidth: 0 }}>
															<Text size="sm" weight={500}>{cmd.label}</Text>
															<Text size="xs" color="dimmed">{cmd.desc}</Text>
														</Box>
														<Box style={{ padding: "1px 7px", background: "rgba(59,130,246,0.15)", borderRadius: 10, fontSize: 11, color: "#60a5fa", flexShrink: 0 }}>
															{T("globalSearchCommandBadge", lang)}
														</Box>
													</Flex>
												</ResultItem>
											);
										})}
									</>
								)}

								{/* Pages */}
								{!slashMeta.isSlash && !activeFilter && menuResults.length > 0 && (
									<>
										{commandResults.length > 0 && <Divider my={4} />}
										<SectionHeader Icon={IconLayoutGrid} label={T("pages", lang)} />
										{menuResults.map((page, i) => {
											const fiKey = `page-${i}`;
											const flatIdx = flatIndexByKey[fiKey] ?? -1;
											const highlighted = hoveredKey === fiKey || flatIdx === activeIndex;
											const fi = { type: "page", key: fiKey, page };
											return (
												<ResultItem
													key={fiKey}
													id={`gs-item-${flatIdx}`}
													colorScheme={colorScheme}
													highlighted={highlighted}
													onMouseEnter={() => { setHoveredKey(fiKey); setActiveIndex(flatIdx); }}
													onMouseLeave={() => setHoveredKey(null)}
													onClick={() => handleOpen(fi)}
												>
													<Flex align="center" justify="space-between" gap="sm">
														<Flex align="center" gap="sm" style={{ minWidth: 0, flex: 1 }}>
															{page.icon ? (
																<Image src={`/assets/${page.icon}`} alt={page.name} style={{ width: 26, height: 26, flexShrink: 0, filter: `invert(${colorScheme === "dark" ? 1 : 0})` }} />
															) : (
																<Avatar size={26} radius="sm" color="indigo"><IconLayoutGrid size={13} /></Avatar>
															)}
															<Text size="sm" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
																{page.parent ? `${page.parent} / ${page.name}` : page.name}
															</Text>
														</Flex>
														{highlighted && (
															<Tooltip label={T("globalSearchOpenInNewTab", lang)} withArrow>
																<ActionIcon size="xs" variant="subtle" onClick={(e) => { e.stopPropagation(); handleOpen(fi, true); }}>
																	<IconExternalLink size={11} />
																</ActionIcon>
															</Tooltip>
														)}
													</Flex>
												</ResultItem>
											);
										})}
									</>
								)}

								{/* Data sections (RBAC-gated by backend) */}
								{dataSections.map((section, secIdx) => {
									const { key: sectionKey, items, cfg } = section;
									const SIcon = cfg.icon;
									const showDiv = secIdx > 0 || (!activeFilter && (commandResults.length > 0 || menuResults.length > 0));
									return (
										<span key={sectionKey}>
											{showDiv && <Divider my={4} />}
											<SectionHeader Icon={SIcon} label={T(sectionTitleKey(sectionKey), lang)} />
											{items.map((item) => {
												const fiKey = `${sectionKey}-${item._id}`;
												const fi = { type: "data", key: fiKey, sectionKey, item, cfg };
												const flatIdx = flatIndexByKey[fiKey] ?? -1;
												const highlighted = hoveredKey === fiKey || flatIdx === activeIndex;
												const title = cfg.getTitle(item, lang);
												const itemSub = cfg.getSub ? cfg.getSub(item, lang) : null;
												const route = cfg.getRoute(item);
												return (
													<ResultItem
														key={fiKey}
														id={`gs-item-${flatIdx}`}
														colorScheme={colorScheme}
														highlighted={highlighted}
														onMouseEnter={() => { setHoveredKey(fiKey); setActiveIndex(flatIdx); }}
														onMouseLeave={() => setHoveredKey(null)}
														onClick={() => handleOpen(fi)}
													>
														<Flex align="center" justify="space-between" gap="sm">
															<Flex align="center" gap="sm" style={{ minWidth: 0, flex: 1 }}>
																<Avatar
																	size={26}
																	radius="sm"
																	color={cfg.color}
																	style={{ flexShrink: 0 }}
																	src={sectionKey === "users" ? getUserImageSrc(item, { targetWidth: 56 }) : undefined}
																>
																	<SIcon size={13} />
																</Avatar>
																<Box style={{ minWidth: 0 }}>
																	<Text size="sm" weight={500} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</Text>
																	{itemSub && <Text size="xs" color="dimmed" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{itemSub}</Text>}
																</Box>
															</Flex>
															{highlighted && (
																<Flex gap={2} style={{ flexShrink: 0 }}>
																	<Tooltip label={T("globalSearchOpenInNewTab", lang)} withArrow>
																		<ActionIcon size="xs" variant="subtle" onClick={(e) => { e.stopPropagation(); handleOpen(fi, true); }}>
																			<IconExternalLink size={11} />
																		</ActionIcon>
																	</Tooltip>
																	<CopyButton value={`${window.location.origin}${route}`}>
																		{({ copied, copy }) => (
																			<Tooltip label={copied ? T("globalSearchCopied", lang) : T("globalSearchCopyLink", lang)} withArrow>
																				<ActionIcon size="xs" variant="subtle" onClick={(e) => { e.stopPropagation(); copy(); }}>
																					<IconLink size={11} />
																				</ActionIcon>
																			</Tooltip>
																		)}
																	</CopyButton>
																</Flex>
															)}
														</Flex>
													</ResultItem>
												);
											})}
										</span>
									);
								})}

							</ul>
						</ScrollArea>
					</Box>

					{/* Right: preview panel (desktop only) */}
					{!isMobile && showPreview && (
						<>
							<Divider orientation="vertical" />
							<Box style={{ width: 260, flexShrink: 0, overflowY: "auto", maxHeight: "60vh" }}>
								<PreviewContent flatItem={activeItem} lang={lang} colorScheme={colorScheme} />
							</Box>
						</>
					)}
				</Flex>

				{/* Footer keyboard hints */}
				<Divider m="0" />
				<Flex
					gap={isMobile ? 6 : 12}
					wrap="wrap"
					style={{ padding: "5px 12px" }}
					justify={isMobile ? "center" : "flex-end"}
				>
					{[
						["↑↓", T("globalSearchNavigate", lang)],
						["↵", T("globalSearchOpen", lang)],
						...(!isMobile ? [["Tab", showPreview ? T("globalSearchHidePreview", lang) : T("globalSearchShowPreview", lang)]] : []),
						["Esc", T("globalSearchClose", lang)],
					].map(([key, label]) => (
						<Flex key={key} align="center" gap={3}>
							<Code style={{ fontSize: 10, padding: "1px 5px" }}>{key}</Code>
							<Text size="xs" color="dimmed">{label}</Text>
						</Flex>
					))}
				</Flex>
			</Modal>

			<Modal
				opened={Boolean(slashModal)}
				onClose={() => setSlashModal(null)}
				title={slashModal?.title || "Slash command"}
				centered
				size="md"
				transitionProps={{ transition: "pop", duration: 200, onEntered: focusSlashModalFirstField }}
			>
				<Stack ref={slashModalContentRef}>
					{slashModal?.id === "task" && (
						<>
							<TextInput
								label="Task title"
								value={slashForm.title}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, title: event.currentTarget.value }))}
							/>
							<Textarea
								label="Description"
								minRows={3}
								value={slashForm.description}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, description: event.currentTarget.value }))}
							/>
						</>
					)}

					{slashModal?.id === "ticket" && (
						<>
							<TextInput
								label="Ticket title"
								value={slashForm.title}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, title: event.currentTarget.value }))}
							/>
							<Textarea
								label="Description"
								minRows={3}
								value={slashForm.description}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, description: event.currentTarget.value }))}
							/>
							<Group grow>
								<Select
									label="Domain"
									value={slashForm.domain || null}
									onChange={(value) => setSlashForm((prev) => ({ ...prev, domain: value || "" }))}
									data={[
										{ value: "it", label: "IT" },
										{ value: "hr", label: "HR" },
										{ value: "finance", label: "Finance" },
										{ value: "facility", label: "Facility" },
										{ value: "security", label: "Security" },
									]}
									clearable
								/>
								<Select
									label="Type"
									value={slashForm.ticketType}
									onChange={(value) => setSlashForm((prev) => ({ ...prev, ticketType: value || "incident" }))}
									data={[
										{ value: "incident", label: "Incident" },
										{ value: "request", label: "Request" },
										{ value: "question", label: "Question" },
									]}
								/>
							</Group>
						</>
					)}

					{slashModal?.id === "meeting" && (
						<>
							<TextInput
								label="Meeting title"
								value={slashForm.title}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, title: event.currentTarget.value }))}
							/>
							<Textarea
								label="Description"
								minRows={3}
								value={slashForm.description}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, description: event.currentTarget.value }))}
							/>
							<Group grow>
								<TextInput
									label="Date"
									type="date"
									value={slashForm.meetingDate}
									onChange={(event) => setSlashForm((prev) => ({ ...prev, meetingDate: event.currentTarget.value }))}
								/>
								<TextInput
									label="Start"
									type="time"
									value={slashForm.meetingStart}
									onChange={(event) => setSlashForm((prev) => ({ ...prev, meetingStart: event.currentTarget.value }))}
								/>
								<TextInput
									label="End"
									type="time"
									value={slashForm.meetingEnd}
									onChange={(event) => setSlashForm((prev) => ({ ...prev, meetingEnd: event.currentTarget.value }))}
								/>
							</Group>
						</>
					)}

					{slashModal?.id === "remind" && (
						<>
							<TextInput
								label="Reminder title"
								value={slashForm.title}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, title: event.currentTarget.value }))}
							/>
							<Textarea
								label="Description"
								minRows={3}
								value={slashForm.description}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, description: event.currentTarget.value }))}
							/>
							<Group grow>
								<TextInput
									label="Date"
									type="date"
									value={slashForm.remindDate}
									onChange={(event) => setSlashForm((prev) => ({ ...prev, remindDate: event.currentTarget.value }))}
								/>
								<TextInput
									label="Time"
									type="time"
									value={slashForm.remindTime}
									onChange={(event) => setSlashForm((prev) => ({ ...prev, remindTime: event.currentTarget.value }))}
								/>
							</Group>
						</>
					)}

					{slashModal?.id === "call" && (
						<>
							<MultiSelect
								label="Participants"
								searchable
								data={userOptions}
								value={slashForm.participants}
								onChange={(value) => setSlashForm((prev) => ({ ...prev, participants: value }))}
							/>
							<Select
								label="Call type"
								value={slashForm.callType}
								onChange={(value) => setSlashForm((prev) => ({ ...prev, callType: value || "audio" }))}
								data={[
									{ value: "audio", label: "Audio" },
									{ value: "video", label: "Video" },
								]}
							/>
						</>
					)}

					{slashModal?.id === "poll" && (
						<>
							<Select
								label="Chat"
								searchable
								data={pollChatOptions}
								value={slashForm.pollChatId || null}
								onChange={(value) => setSlashForm((prev) => ({ ...prev, pollChatId: value || "" }))}
							/>
							<TextInput
								label="Question"
								value={slashForm.pollQuestion}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, pollQuestion: event.currentTarget.value }))}
							/>
							<Textarea
								label="Description"
								minRows={2}
								value={slashForm.description}
								onChange={(event) => setSlashForm((prev) => ({ ...prev, description: event.currentTarget.value }))}
							/>
							{slashForm.pollOptions.map((option, index) => (
								<TextInput
									key={`poll-option-${index}`}
									label={`Option ${index + 1}`}
									value={option}
									onChange={(event) =>
										setSlashForm((prev) => ({
											...prev,
											pollOptions: prev.pollOptions.map((item, itemIndex) =>
												itemIndex === index ? event.currentTarget.value : item
											),
										}))
									}
								/>
							))}
							<Button
								variant="subtle"
								leftIcon={<IconPlus size={14} />}
								onClick={() =>
									setSlashForm((prev) => ({
										...prev,
										pollOptions: [...prev.pollOptions, ""],
									}))
								}
							>
								Add option
							</Button>
						</>
					)}

					<Group position="right" mt="sm">
						<Button variant="subtle" onClick={() => setSlashModal(null)}>
							Cancel
						</Button>
						<Button loading={slashSubmitting} onClick={submitSlashModal}>
							Run
						</Button>
					</Group>
				</Stack>
			</Modal>
		</>
	);
}

export default GlobalSearch;
